import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Users, Trophy, Award, Globe, CheckCircle, ArrowRight, MapPin } from 'lucide-react';
import { highlights, courses } from '../mockData';
import HeroSlider from '../components/HeroSlider';
import AnimatedCounter from '../components/AnimatedCounter';

const Home = ({ introComplete = false }) => {
  const whyChooseUs = [
    {
      title: "Structured Training",
      description: "Comprehensive curriculum designed to cover every aspect of international dental exams",
      icon: CheckCircle
    },
    {
      title: "Real Exam Simulation",
      description: "3-5 mock exams in realistic environments that replicate actual examination conditions",
      icon: Trophy
    },
    {
      title: "Ethical Mentoring",
      description: "Honest, professional guidance focused on your success through consistent practice",
      icon: Award
    },
    {
      title: "Personalized Feedback",
      description: "Rubric-based evaluation and continuous feedback to identify and improve weak areas",
      icon: Users
    }
  ];

  const iconMap = {
    users: Users,
    trophy: Trophy,
    award: Award,
    globe: Globe
  };

  return (
    <div className="min-h-screen">
      {/* Hero Slider */}
      <HeroSlider introComplete={introComplete} />

      {/* Detailed Sections Referenced by Slider */}
      
      {/* ADC Part 2 Details Section */}
      <section id="adc-details" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                ADC Part 2 Practical Excellence
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Our comprehensive 35-day intensive training program is specifically designed for the Australian Dental Council Part 2 Practical Examination. With 13+ years of experience, we've perfected our training methodology.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>30 Working Days</strong> of intensive hands-on training</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>3-5 Mock Exams</strong> with detailed feedback and assessment</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>Expert Instructors</strong> with proven track record</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>Exact ADC Setup</strong> with original manikins and equipment</span>
                </li>
              </ul>
              <Button asChild className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90">
                <Link to="/courses/adc-part-2">View Full Course Details <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
            <div className="bg-gradient-to-br from-[#1e3a8a] to-[#3b82f6] p-8 rounded-2xl text-white">
              <h3 className="text-2xl font-bold mb-6">What's Covered</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">Technical Tasks Day:</h4>
                  <p className="text-sm text-gray-200">PFM Crown, Provisional Crown, Composites (Class II & IV), Amalgam, Cavity Prep, Endo Access</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Clinical Skills Day:</h4>
                  <p className="text-sm text-gray-200">Radiographs, Rubber Dam, Scaling, Extraction, LA, Medical Emergencies, Sealants, Band & Retainer</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Schedule:</h4>
                  <p className="text-sm text-gray-200">Mon-Fri: 10 AM - 7 PM | Sat: 10 AM - 2 PM</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Facility Details Section */}
      <section id="facility-details" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              State-of-the-Art Training Facility
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Experience training in the most realistic ADC exam simulation environment with original equipment and manikins
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <Card className="border-2 hover:border-[#7c3aed] transition-colors">
              <CardHeader>
                <CardTitle>Original Equipment</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#7c3aed] flex-shrink-0 mt-0.5" />
                    <span>CPR Training Manikins</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#7c3aed] flex-shrink-0 mt-0.5" />
                    <span>LA Practice Manikins</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#7c3aed] flex-shrink-0 mt-0.5" />
                    <span>Radiograph Equipment</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#7c3aed] flex-shrink-0 mt-0.5" />
                    <span>Complete Clinical Stations</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            <Card className="border-2 hover:border-[#7c3aed] transition-colors">
              <CardHeader>
                <CardTitle>Training Environment</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#7c3aed] flex-shrink-0 mt-0.5" />
                    <span>Exact Exam Simulation Setup</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#7c3aed] flex-shrink-0 mt-0.5" />
                    <span>Spacious Modern Facility</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#7c3aed] flex-shrink-0 mt-0.5" />
                    <span>Well-Equipped Dental Units</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#7c3aed] flex-shrink-0 mt-0.5" />
                    <span>All Materials Provided</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            <Card className="border-2 hover:border-[#7c3aed] transition-colors">
              <CardHeader>
                <CardTitle>Location Benefits</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#7c3aed] flex-shrink-0 mt-0.5" />
                    <span>Chandigarh - Planned City</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#7c3aed] flex-shrink-0 mt-0.5" />
                    <span>Well Connected (Air/Rail/Road)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#7c3aed] flex-shrink-0 mt-0.5" />
                    <span>Student-Friendly Environment</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#7c3aed] flex-shrink-0 mt-0.5" />
                    <span>Accommodation Nearby</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
          <div className="text-center">
            <Button asChild size="lg" className="bg-[#7c3aed] hover:bg-[#7c3aed]/90">
              <Link to="/gallery">View Gallery <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Success Stories Details Section */}
      <section id="success-details" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              13+ Years of Proven Excellence
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our reputation is built on results, not marketing. Join the success story of 1000+ dentists who achieved their international career goals with ODA.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12 max-w-5xl mx-auto">
            <div className="bg-white p-8 rounded-lg shadow-sm text-center">
              <div className="text-4xl font-bold text-[#dc2626] mb-2">1000+</div>
              <div className="text-gray-600">Dentists Trained</div>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm text-center">
              <div className="text-4xl font-bold text-[#dc2626] mb-2">13+</div>
              <div className="text-gray-600">Years Experience</div>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm text-center">
              <div className="text-4xl font-bold text-[#dc2626] mb-2">25+</div>
              <div className="text-gray-600">Countries</div>
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="bg-gradient-to-br from-[#dc2626] to-[#f87171] p-8 rounded-2xl text-white">
              <h3 className="text-2xl font-bold mb-6">Our Story</h3>
              <p className="mb-4 text-gray-100">
                We started with just 18 candidates in our first batch - 17 of them passed. This remarkable success was not achieved through aggressive marketing or tall promises, but through structured training, honest guidance, and deep commitment to student success.
              </p>
              <p className="text-gray-100">
                Word of mouth became our strongest advocate. Today, we stand as a trusted name in international dental exam preparation, having trained over 1000 dentists from around the world.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">What Makes Us Different</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-[#dc2626] rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-bold">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Ethical Training</h4>
                    <p className="text-sm text-gray-600">No false promises, just honest guidance and realistic expectations</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-[#dc2626] rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-bold">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Realistic Simulation</h4>
                    <p className="text-sm text-gray-600">Training in environments that replicate actual exam conditions</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-[#dc2626] rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-bold">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Continuous Feedback</h4>
                    <p className="text-sm text-gray-600">Regular assessment and detailed feedback to track progress</p>
                  </div>
                </li>
              </ul>
              <div className="mt-8">
                <Button asChild className="bg-[#dc2626] hover:bg-[#dc2626]/90">
                  <Link to="/testimonials">Read Success Stories <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights Section - Animated Statistics */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {highlights.map((highlight, index) => {
              const IconComponent = iconMap[highlight.icon];
              return (
                <motion.div
                  key={highlight.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center group"
                >
                  <div className="relative inline-flex items-center justify-center w-20 h-20 mb-4 mx-auto">
                    {/* Animated glow ring */}
                    <motion.div
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.3, 0.6, 0.3]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                      className="absolute inset-0 rounded-full bg-gradient-to-r from-[#00B7B2] to-[#7FE7E3] blur-xl"
                    />
                    <div className="relative w-16 h-16 bg-gradient-to-br from-[#1e3a8a] to-[#00B7B2] rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <div className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-[#1e3a8a] via-[#00B7B2] to-[#7FE7E3] bg-clip-text text-transparent mb-2">
                    {highlight.number.includes('+') ? (
                      <>
                        <AnimatedCounter end={highlight.number.replace('+', '')} />+
                      </>
                    ) : (
                      highlight.number
                    )}
                  </div>
                  <div className="text-sm lg:text-base text-gray-600 font-medium">
                    {highlight.label}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Courses Overview Section */}
      <section className="py-20 relative overflow-hidden">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#071B34] via-[#0d2847] to-[#071B34] opacity-5" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Training Programs
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive preparation courses for international dental licensing examinations
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {courses.map((course, index) => (
              <motion.div
                key={course.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
              >
                <Card className="h-full hover:shadow-2xl transition-all duration-300 border-2 hover:border-[#00B7B2] glass-card-dark backdrop-blur-sm bg-white/95 relative overflow-hidden group">
                  {/* Hover glow effect */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#00B7B2]/10 to-transparent" />
                  </div>
                  
                  <CardHeader className="relative z-10">
                    {course.popular && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="inline-block px-3 py-1 bg-gradient-to-r from-[#00B7B2] to-[#7FE7E3] text-white text-xs font-semibold rounded-full mb-2 w-fit shadow-lg"
                      >
                        Most Popular
                      </motion.div>
                    )}
                    <CardTitle className="text-xl group-hover:text-[#00B7B2] transition-colors">
                      {course.title}
                    </CardTitle>
                    <CardDescription className="text-sm">{course.shortDesc}</CardDescription>
                  </CardHeader>
                  <CardContent className="relative z-10">
                    <div className="space-y-2 mb-4">
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold">Duration:</span> {course.duration}
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold">Mode:</span> {course.mode}
                      </p>
                    </div>
                    <Button 
                      asChild 
                      className="w-full bg-gradient-to-r from-[#1e3a8a] to-[#00B7B2] hover:from-[#00B7B2] hover:to-[#7FE7E3] text-white border-0 btn-premium"
                    >
                      <Link to={`/courses/${course.id}`}>
                        Learn More <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose ODA
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experience the difference that quality training makes
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="bg-white p-6 rounded-xl shadow-sm hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-[#00B7B2] relative overflow-hidden group"
              >
                {/* Animated background gradient on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#00B7B2]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                <div className="relative z-10">
                  <motion.div
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                    className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-[#1e3a8a] to-[#00B7B2] rounded-xl mb-4 shadow-lg"
                  >
                    <item.icon className="h-7 w-7 text-white" />
                  </motion.div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-[#00B7B2] transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Chandigarh Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Chandigarh?
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-[#1e3a8a] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Clean & Planned City</h3>
                    <p className="text-gray-600">India's first planned city with wide roads, organized sectors, and excellent infrastructure</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-[#1e3a8a] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Well Connected</h3>
                    <p className="text-gray-600">International airport, major railway junction, and excellent road connectivity to all parts of India</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-[#1e3a8a] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Student-Friendly Environment</h3>
                    <p className="text-gray-600">Safe, affordable, and peaceful environment ideal for focused exam preparation</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-[#1e3a8a] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Quality of Life</h3>
                    <p className="text-gray-600">Pleasant weather, green spaces, and all modern amenities for comfortable living</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-[#1e3a8a]/5 rounded-2xl p-8 border-2 border-[#1e3a8a]/20">
              <div className="aspect-video bg-white rounded-lg shadow-inner flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="h-24 w-24 mx-auto mb-4 text-[#1e3a8a]" />
                  <p className="text-xl font-semibold text-gray-700">SCO 224-225, Sector 40D</p>
                  <p className="text-gray-600">Chandigarh, India</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 relative overflow-hidden">
        {/* Animated gradient background */}
        <motion.div
          animate={{
            backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute inset-0 bg-gradient-to-r from-[#071B34] via-[#1e3a8a] via-[#00B7B2] to-[#071B34] bg-[length:200%_100%]"
        />
        
        {/* Overlay pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Start Your Journey Today
            </h2>
            <p className="text-lg text-gray-200 mb-8 max-w-2xl mx-auto">
              Join 1000+ successful dentists who achieved their international licensing goals with ODA
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-white text-[#1e3a8a] hover:bg-gray-100 btn-premium shadow-2xl"
                >
                  <Link to="/courses">View All Courses</Link>
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button 
                  asChild 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white/10 backdrop-blur-sm"
                >
                  <Link to="/contact">Get in Touch</Link>
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
